import { formatBoolean } from "./booleanutils";
import { formatDate } from "./dateutils";
import { formatNumber, getFixedDecimalFormatNumber } from "./numberutils";
import { formatRuntime } from "./runtimeutils";
import { getColumnText, getIconHint, getPhrase } from "./phrases";
import { toCamelCase } from './utils';

function toIconFor(entityName) {
	return function(value) {
		if (!value) {
			return null;
		}
		return {
			iconId: value,
			icon: value,
			hint: getIconHint(value, entityName)
		};
	}
}

function status2Class(value) {
	if (value === 'nok' || value === 'missing' || value === 'duplicate') {
		return 'error';
	} else if (value === 'ok' || value === 'processed') {
		return 'ok';
	} else if (value === 'warning') {
		return 'warning';
	}
}

function formatStatus(value) {
	if (typeof value === 'string') {
		value = value.toLowerCase();
		return {
			className: status2Class(value),
			text: getPhrase('status.' + value)
		};
	} else {
		return value;
	}
}

function getParrProcessor(phraseIdPfx) {
	phraseIdPfx = phraseIdPfx || '';
	return function(value) {
		if (typeof value !== 'string') {
			return '';
		}
		return value.split(/\|/g).filter(Boolean).map(item => getPhrase(phraseIdPfx + item)).join(', ');
	};
}

// mutates input!
export function normalizeResultSet(resultSet, options) {
	options = options || {};
	const entityName = options.entityName;
	let sortableColumns = resultSet.sortableColumns || {};
	for (let key in sortableColumns) {
		let idx = key.lastIndexOf('$');
		if (idx > 0) {
			sortableColumns[key.substring(idx + 1)] = sortableColumns[key];
		}
	}
	let orderByMap = {};
	if (resultSet.orderBy) {
		resultSet.orderBy.forEach((col, index) => {
			let sortIndex = index + 1;
			if (col.startsWith('-')) {
				col = col.substring(1);
				sortIndex = -sortIndex;
			}
			orderByMap[col] = sortIndex;
		});
	}
	const columns = resultSet.columns.map(col => {
		if (typeof col === 'string') {
			let name = col || '';
			let type = 'default';
			let process = null;
			if (name.startsWith('d$')) {
				type = 'date';
				name = name.substring(2);
				process = formatDate;
			} else if (name.startsWith('n$')) {
				type = 'number';
				name = name.substring(2);
				let idx = name.indexOf('$');
				if (idx < 0) {
					process = formatNumber;
				} else {
					let decimalDigits = parseInt(name.substring(0, idx));
					process = getFixedDecimalFormatNumber(decimalDigits);
					name = name.substring(idx + 1);
				}
			} else if (name.startsWith('b$')) {
				type = 'boolean';
				name = name.substring(2);
				process = formatBoolean;
/*			} else if (name.startsWith('m$')) {
				type = 'month';
				name = name.substring(2);
				process = getMonthName;*/
			} else if (name.startsWith('i$')) {
				type = 'icon',
				name = name.substring(2);
				process = toIconFor(entityName);
			} else if (name.startsWith('r$')) {
				type = 'runtime';
				name = name.substring(2);
				process = formatRuntime;
			} else if (name.startsWith('status$')) {
				type = 'status';
				name = name.substring(7);
				process = formatStatus;
			} else if (name.startsWith('p$')) {
				type = 'string';
				name = name.substring(2);
				process = getPhrase
			} else if (name.startsWith('parr$')) {
				type = 'string';
				name = name.substring(5);
				let idx = name.indexOf('$');
				if (idx < 0) {
					process = getParrProcessor();
				} else {
					process = getParrProcessor(name.substring(0, idx));
					name = name.substring(idx + 1);
				}
			}

			return {
				name,
				sortable: sortableColumns[name],
				sortIndex: orderByMap[name],
				type,
				text: type === 'icon' ? ' ' : getColumnText(name, entityName, true),
				title: getColumnText(name, entityName),
				process
			}
		} else {
			return col;
		}
	});
	let rows = resultSet.rows;
	for (let i=0; i<rows.length; i++) {
		let cells = rows[i].cells;
		for (let c = 0; c < cells.length; c++) {
			const process = columns[c].process;
			if (process) {
				cells[c] = process(cells[c]);
			}
		}
	}

	//console.log('columns = ', columns);
	resultSet.columns = columns;
	return resultSet;
}

function getNull() {
	return null;
}

export function resultSetFromObjectArray(arr, options) {
	options = options || {};
	const entityName = options.entityName;
	const processCell = (typeof options.processCell === 'function' ? options.processCell : null);
	const processObject = (typeof options.processCell === 'function' ? options.processObject : null);
	const getRowClass = (typeof options.getRowClass === 'function' ? options.getRowClass : getNull);
	const withObject = options.withObject;
	let columns = options.columns.map(col => {
		if (typeof col === 'string') {
			return {
				name: col,
				text: getColumnText(col, entityName, true),
				title: getColumnText(col, entityName)
			};
		} else {
			return col;
		}
	});
	const keys = columns.map(col => toCamelCase(col.name));
	let rows = arr.map(obj => {
		let cells = [];
		keys.forEach(key => {
			let value = obj[key];
			if (processCell) {
				value = processCell(key, value);
			}
			cells.push(value);
		});
		let res = {
			class: getRowClass(obj),
			cells
		};
		if (processObject) {
			res.object = processObject(obj);
		}
		return res;
	});
	return {
		columns,
		rows
	};
}
